import { Alert, Badge, Button, Flex, Heading, Loader, SwitchField, Text, View } from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import { API, Auth } from "aws-amplify";
import { useLocation, useNavigate } from "react-router-dom";
import { feeLabel, isPubliclyReachable, publicListingUrl, statusLabel } from "./listingModel";
import { PublicListingsSection } from "./PublicListingsSection";

async function authRequest(body) {
    const user = await Auth.currentAuthenticatedUser();
    const request = { headers: { Authorization: `${user.signInUserSession.idToken.jwtToken}` } };
    if (body) request.body = body;
    return request;
}

function apiErrorMessage(error, fallback) {
    const msg = error && error.response && error.response.data && error.response.data.error;
    return typeof msg === "string" && msg ? msg : fallback;
}

export function Listings({ balance, updateBalance, subscription, clientData }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [actionError, setActionError] = useState("");
    const [busySlug, setBusySlug] = useState("");
    const [showArchived, setShowArchived] = useState(false);
    const [saved, setSaved] = useState((location.state && location.state.saved) || null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const resp = await API.get("gcAPI", "/listings", await authRequest());
                if (active) setListings(Array.isArray(resp) ? resp : []);
            } catch (error) {
                console.error("Error fetching listings", error);
                if (active) setLoadError("Could not load listings");
            }
            if (active) setLoading(false);
        })();
        return () => { active = false; };
    }, []);

    async function toggleArchive(l) {
        if (busySlug) return;
        setBusySlug(l.slug);
        setActionError("");
        try {
            const body = { action: "archive", slug: l.slug, archived: !l.archived };
            const resp = await API.post("gcAPI", "/listings", await authRequest(body));
            if (resp && resp.listing) setListings((prev) => prev.map((x) => (x.slug === l.slug ? resp.listing : x)));
        } catch (error) {
            console.error("Error toggling archive", error);
            setActionError(apiErrorMessage(error, "Could not update listing"));
        }
        setBusySlug("");
    }

    const visible = listings.filter((l) => Boolean(l.archived) === showArchived);

    return (
        <View align="center">
            <View maxWidth="1000px" align="left">
                {saved && (
                    <Alert variation="success" isDismissible onDismiss={() => setSaved(null)} marginBottom="15px">
                        {isPubliclyReachable(saved) ? (
                            <>Saved. Public URL: <a href={publicListingUrl(saved.slug)} target="_blank" rel="noreferrer">{publicListingUrl(saved.slug)}</a></>
                        ) : "Saved (private)"}
                    </Alert>
                )}
                <Flex justifyContent="space-between" alignItems="center" marginBottom="20px">
                    <Heading level={3} color="#414c5a">Listings</Heading>
                    <Button variation="primary" onClick={() => navigate("/listings/new")}>New listing</Button>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center" marginBottom="10px">
                    <Heading level={4}>My listings</Heading>
                    <SwitchField label="Show archived" isChecked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                </Flex>
                {loadError && <Alert variation="error" marginBottom="15px">{loadError}</Alert>}
                {actionError && <Alert variation="error" isDismissible onDismiss={() => setActionError("")} marginBottom="15px">{actionError}</Alert>}
                <Flex direction="column" gap="15px">
                    {loading ? <Loader variation="linear" /> : !loadError && visible.length === 0 ? (
                        <Text>No listings yet.</Text>
                    ) : visible.map((l) => (
                        <Flex key={l.slug} direction="row" justifyContent="space-between" alignItems="center" wrap="wrap"
                            padding="15px" border="1px solid #ccc" borderRadius="8px">
                            <View>
                                <Flex alignItems="center" gap="8px" wrap="wrap">
                                    <Heading level={5}>{l.title}</Heading>
                                    <Badge variation="info">{l.listingType}</Badge>
                                    <Badge variation={isPubliclyReachable(l) ? "success" : "warning"}>{statusLabel(l)}</Badge>
                                </Flex>
                                <Text>{feeLabel(l)}</Text>
                                <Text variation="secondary">{Array.isArray(l.engagementTypes) ? l.engagementTypes.join(" · ") : ""}</Text>
                                {isPubliclyReachable(l) && (
                                    <a href={publicListingUrl(l.slug)} target="_blank" rel="noreferrer">{publicListingUrl(l.slug)}</a>
                                )}
                            </View>
                            <Flex gap="8px">
                                <Button size="small" onClick={() => navigate(`/listings/edit/${l.slug}`)}>Edit</Button>
                                <Button size="small" isLoading={busySlug === l.slug} isDisabled={Boolean(busySlug)} onClick={() => toggleArchive(l)}>
                                    {l.archived ? "Restore" : "Archive"}
                                </Button>
                            </Flex>
                        </Flex>
                    ))}
                </Flex>
                <PublicListingsSection />
            </View>
        </View>
    );
}
